import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ToastProvider, useToast } from './contexts/ToastContext';
import { JobDescriptionProvider } from './contexts/JobDescriptionContext';
import { ResumeProvider } from './contexts/ResumeContext';
import { CreditProvider } from './contexts/CreditContext';
import { setAuthContext } from './utils/api';
import { HomePage } from './components/Home/HomePage';
import { AuthPage } from './components/Auth/AuthPage';
import { Dashboard } from './components/Dashboard/Dashboard';
import Navigation from './components/Layout/Navigation';
import { Footer } from './components/Layout/Footer';
import TransactionsPage from './pages/TransactionsPage';
import BuyCreditsPage from './pages/BuyCreditsPage';
import TermsPage from './pages/TermsPage';
import PrivacyPage from './pages/PrivacyPage';
import RefundsPage from './pages/RefundsPage';
import ContactPage from './pages/ContactPage';
import FAQPage from './pages/FAQPage';
import ToastContainer from './components/ToastContainer';
import AuthCallbackPage from './components/Auth/AuthCallbackPage';

// Protected Route Component with Navigation
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, loading, session } = useAuth();
  
  // Set auth context in API client
  React.useEffect(() => {
    setAuthContext({ user, session });
  }, [user, session]);
  
  if (loading) {
    return (
      <div className="min-h-screen bg-base-200 flex items-center justify-center">
        <span className="loading loading-spinner loading-lg"></span>
      </div>
    );
  }
  
  return user ? (
    <div className="min-h-screen bg-base-200 flex flex-col">
      <JobDescriptionProvider>
        <ResumeProvider>
          <CreditProvider>
            <Navigation />
            <main className="flex-1 pt-4">
              {children}
            </main>
            <Footer />
          </CreditProvider>
        </ResumeProvider>
      </JobDescriptionProvider>
    </div>
  ) : <Navigate to="/auth" replace />;
};

// Public Route Component (redirects to dashboard if already authenticated)
const PublicRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-base-200 flex items-center justify-center">
        <span className="loading loading-spinner loading-lg"></span>
      </div>
    );
  }
  
  return user ? <Navigate to="/dashboard" replace /> : <>{children}</>;
};

// Main App Content
const AppContent: React.FC = () => {
  const { toasts, removeToast } = useToast();

  // Initialize theme on app load
  React.useEffect(() => {
    const savedTheme = localStorage.getItem('theme') || 'light';
    document.documentElement.setAttribute('data-theme', savedTheme);
  }, []);

  return (
    <Router>
      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route 
          path="/auth" 
          element={
            <PublicRoute>
              <AuthPage />
            </PublicRoute>
          } 
        />
        <Route 
          path="/auth/callback" 
          element={
            <AuthCallbackPage />
          } 
        />
        <Route 
          path="/dashboard" 
          element={
            <ProtectedRoute>
              <Dashboard />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/transactions" 
          element={
            <ProtectedRoute>
              <TransactionsPage />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/buy-credits" 
          element={
            <ProtectedRoute>
              <BuyCreditsPage />
            </ProtectedRoute>
          } 
        />
        <Route path="/terms" element={<TermsPage />} />
        <Route path="/privacy" element={<PrivacyPage />} />
        <Route path="/refunds" element={<RefundsPage />} />
        <Route path="/contact" element={<ContactPage />} />
        <Route path="/faq" element={<FAQPage />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      
      <ToastContainer toasts={toasts} onClose={removeToast} />
    </Router>
  );
};

// Main App Component
function App() {
  return (
    <AuthProvider>
      <ToastProvider>
        <AppContent />
      </ToastProvider>
    </AuthProvider>
  );
}

export default App;
